import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Target, Zap, TrendingUp, DollarSign, Layers, Info, Loader2, AlertCircle } from 'lucide-react';
import { leadsAPI } from '../../../api/apiHelper';

export default function Products() {
    const { id } = useParams();
    const dealId = id; // Use the ID from the URL

    const [activeTab, setActiveTab] = useState("Product Interest");
    const [productData, setProductData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchProductInsights = async () => {
            if (!dealId || dealId === 'view') {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                // console.log("Fetching product insights for:", dealId);
                const response = await leadsAPI.getDealProductInsights(dealId);
                // console.log("Product Insights Response:", response);

                if (response && response.data) {
                    setProductData(response.data);
                } else if (response && response.productInterested) {
                    // Fallback if data is not wrapped in 'data' prop but is at root (unlikely given other tabs)
                    setProductData(response);
                } else {
                    // If response suggests empty or different structure
                    console.warn("Unexpected product data structure:", response);
                    setProductData(null);
                }

            } catch (err) {
                console.error("Error fetching product insights:", err);
                setError("Failed to load product insights.");
            } finally {
                setLoading(false);
            }
        };

        fetchProductInsights();
    }, [dealId]);

    const tabs = [
        { id: "Product Interest", label: "Product Interest", icon: <Target size={ 16 } /> },
        { id: "Requested Features", label: "Requested Features", icon: <Layers size={ 16 } /> },
        { id: "Cross Sell Opportunities", label: "Cross Sell Opportunities", icon: <Zap size={ 16 } /> },
        { id: "Up Sell Opportunities", label: "Up Sell Opportunities", icon: <DollarSign size={ 16 } /> },
        { id: "Potential Score", label: "Potential Score", icon: <TrendingUp size={ 16 } /> }
    ];

    const renderLoadingState = () => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: '300px',
            color: '#6b7280'
        } }>
            <Loader2 className="animate-spin" size={ 32 } style={ { marginBottom: '16px', color: '#3b82f6' } } />
            <div style={ { fontSize: '14px', fontWeight: 500 } }>Loading product insights...</div>
        </div>
    );

    const renderErrorState = () => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: '300px',
            color: '#ef4444',
            textAlign: 'center',
            padding: '20px'
        } }>
            <AlertCircle size={ 32 } style={ { marginBottom: '16px' } } />
            <div style={ { fontSize: '14px', fontWeight: 500, marginBottom: '8px' } }>Unable to load data</div>
            <div style={ { fontSize: '12px', color: '#6b7280' } }>
                { error || "Please check your connection and try again." }
            </div>
            <button
                onClick={ () => window.location.reload() }
                style={ {
                    marginTop: '16px',
                    padding: '8px 16px',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    color: '#374151'
                } }
            >
                Retry
            </button>
        </div>
    );

    const renderEmptyState = (message, icon = <Info size={ 24 } />) => (
        <div style={ {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            color: '#9ca3af',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px dashed #e5e7eb',
            textAlign: 'center',
            flex: 1
        } }>
            <div style={ { opacity: 0.5, marginBottom: '12px' } }>{ icon }</div>
            <div style={ { fontSize: '14px', fontWeight: 500 } }>{ message }</div>
        </div>
    );

    const renderContent = () => {
        if (loading) return renderLoadingState();
        if (error) return renderErrorState();
        if (!productData) return renderEmptyState("No input");

        switch (activeTab) {
            case "Product Interest":
                const interestData = productData?.productInterested;
                // API returns array of strings, e.g. ["galileo.io", "CRM Pro"]
                if (!interestData || !Array.isArray(interestData) || interestData.length === 0) return renderEmptyState("No input");

                const interestScore = productData?.productInterestScore || 0;
                const interestLevel = interestScore > 75 ? "High" : interestScore > 40 ? "Medium" : "Low";

                return (
                    <div style={ { padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', height: '100%' } }>
                        <div style={ { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' } }>
                            <div style={ { padding: '10px', background: '#ecfdf5', borderRadius: '8px', color: '#059669' } }>
                                <Target size={ 24 } />
                            </div>
                            <div>
                                <h3 style={ { margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' } }>Product Interest</h3>
                                <span style={ { fontSize: '13px', color: '#6b7280' } }>Based on recent interactions</span>
                            </div>
                        </div>

                        <div style={ { marginBottom: '24px' } }>
                            <span style={ {
                                background: interestLevel === 'High' ? '#dcfce7' :
                                    interestLevel === 'Medium' ? '#fff7ed' : '#f3f4f6',
                                color: interestLevel === 'High' ? '#166534' :
                                    interestLevel === 'Medium' ? '#c2410c' : '#374151',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                marginRight: '10px'
                            } }>
                                Level: { interestLevel }
                            </span>
                            <span style={ { fontSize: '13px', color: '#6b7280', fontWeight: 500 } }>
                                Score: { interestScore }/100 
                            </span>
                        </div>

                        <div style={ { display: 'flex', flexDirection: 'column', gap: '8px' } }>
                            <h4 style={ { margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#374151' } }>Interested Products:</h4>
                            { interestData.map((product, idx) => (
                                <div key={ idx } style={ { padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6', fontSize: '14px', color: '#1f2937' } }>
                                    { product }
                                </div>
                            )) }
                        </div>
                    </div>
                );

            case "Requested Features":
                const features = productData?.requestedFeatures;
                // API returns array of strings
                if (!features || !Array.isArray(features) || features.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' } }>
                        { features.map((feature, index) => (
                            <div key={ index } style={ { padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', transition: 'all 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' } }>
                                <div style={ { display: 'flex', alignItems: 'flex-start', gap: '16px' } }>
                                    <div style={ { marginTop: '4px', padding: '8px', background: '#eff6ff', borderRadius: '8px', color: '#2563eb' } }>
                                        <Layers size={ 20 } />
                                    </div>
                                    <div>
                                        <h4 style={ { margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600, color: '#111827' } }>
                                            { feature }
                                        </h4>
                                        <div style={ { fontSize: '13px', color: '#6b7280' } }>Requested during recent calls</div>
                                    </div>
                                </div>
                            </div>
                        )) }
                    </div>
                );

            case "Cross Sell Opportunities":
                const crossSell = productData?.crossSellOpportunities;
                // API returns array of strings
                if (!crossSell || !Array.isArray(crossSell) || crossSell.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', height: '100%' } }>
                        <div style={ { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' } }>
                            <div style={ { padding: '10px', background: '#fffbeb', borderRadius: '8px', color: '#d97706' } }>
                                <Zap size={ 24 } />
                            </div>
                            <h3 style={ { margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' } }>Cross-Sell Opportunities</h3>
                        </div>

                        <div style={ { display: 'flex', flexDirection: 'column', gap: '12px' } }>
                            { crossSell.map((opp, idx) => (
                                <div key={ idx } style={ {
                                    padding: '16px',
                                    background: '#fffbeb',
                                    borderRadius: '8px',
                                    border: '1px solid #fcd34d',
                                    color: '#92400e',
                                    fontSize: '15px',
                                    fontWeight: 500
                                } }>
                                    { opp }
                                </div>
                            )) }
                        </div>
                    </div>
                );

            case "Up Sell Opportunities":
                const upSell = productData?.upSellOpportunities;
                // API returns array of strings
                if (!upSell || !Array.isArray(upSell) || upSell.length === 0) return renderEmptyState("No input");

                return (
                    <div style={ { padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', height: '100%' } }>
                        <div style={ { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' } }>
                            <div style={ { padding: '10px', background: '#ecfdf5', borderRadius: '8px', color: '#059669' } }>
                                <DollarSign size={ 24 } />
                            </div>
                            <h3 style={ { margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' } }>Up-Sell Opportunities</h3>
                        </div>

                        <div style={ { display: 'flex', flexDirection: 'column', gap: '12px' } }>
                            { upSell.map((opp, idx) => (
                                <div key={ idx } style={ {
                                    padding: '16px',
                                    background: '#ecfdf5',
                                    borderRadius: '8px',
                                    border: '1px solid #6ee7b7',
                                    color: '#065f46',
                                    fontSize: '15px',
                                    fontWeight: 500
                                } }>
                                    { opp }
                                </div>
                            )) }
                        </div>
                    </div>
                );

            case "Potential Score":
                const scoreValue = productData?.potentialScore;
                if (typeof scoreValue !== 'number') return renderEmptyState("No input");

                // Derive interpretation based on score
                let interpretation = "Moderate Potential";
                if (scoreValue >= 80) interpretation = "Very Highest Potential";
                else if (scoreValue >= 60) interpretation = "High Potential";
                else if (scoreValue < 40) interpretation = "Low Potential";

                return (
                    <div style={ { padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' } }>

                        <div style={ { position: 'relative', width: '160px', height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' } }>
                            {/* Simulated Circular Progress */ }
                            <svg width="160" height="160" viewBox="0 0 160 160" style={ { transform: 'rotate(-90deg)' } }>
                                <circle cx="80" cy="80" r="70" stroke="#f3f4f6" strokeWidth="12" fill="none" />
                                <circle cx="80" cy="80" r="70" stroke="#3b82f6" strokeWidth="12" fill="none"
                                    strokeDasharray="440"
                                    strokeDashoffset={ 440 - (440 * scoreValue) / 100 }
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div style={ { position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
                                <span style={ { fontSize: '36px', fontWeight: 700, color: '#111827' } }>{ scoreValue }</span>
                                <span style={ { fontSize: '12px', color: '#6b7280', fontWeight: 500 } }>SCORE</span>
                            </div>
                        </div>

                        <h3 style={ { fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '8px' } }>
                            { interpretation }
                        </h3>

                        <p style={ { fontSize: '14px', color: '#6b7280', maxWidth: '400px', lineHeight: '1.5' } }>
                            Based on product fit, feature requests, and engagement levels.
                        </p>

                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div style={ { height: '100%', display: 'flex', flexDirection: 'column', gap: '8px' } }>

            {/* Sub-Navbar */ }
            <div style={ {
                display: 'flex',
                gap: '8px',
                background: '#fff',
                padding: '0 4px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                overflowX: 'auto',
                flexShrink: 0
            } }>
                { tabs.map(tab => (
                    <button
                        key={ tab.id }
                        onClick={ () => setActiveTab(tab.id) }
                        style={ {
                            padding: '10px 16px',
                            border: 'none',
                            background: activeTab === tab.id ? '#f3f4f6' : 'transparent',
                            color: activeTab === tab.id ? '#1f2937' : '#6b7280',
                            fontSize: '13px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            borderRadius: '6px',
                            margin: '6px 0',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap'
                        } }
                    >
                        { tab.icon }
                        { tab.label }
                    </button>
                )) }
            </div>

            {/* Content Area */ }
            <div style={ { flex: 1, minHeight: 0, overflowY: 'auto' } }>
                { renderContent() }
            </div>

        </div>
    );
}
